import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import FloatingField, { normalizeFloatingLabel } from './FloatingField';

export default function Dropdown({
  value,
  options,
  onChange,
  placeholder,
  label,
  menuLabel,
  error,
  disabled = false,
  className = '',
  buttonClassName = '',
  itemClassName = '',
  menuClassName = '',
  menuHeaderClassName = '',
  preferredDirection = 'auto',
  multiple = false
}) {
  const [open, setOpen] = useState(false);
  const [direction, setDirection] = useState('down');
  const ref = useRef(null);

  const selectedValues = multiple
    ? new Set((Array.isArray(value) ? value : []).map(item => `${item}`))
    : new Set([`${value}`]);
  const selectedOptions = options.filter(opt => selectedValues.has(`${opt.value}`));
  const selected = multiple
    ? (selectedOptions.length === 1 ? selectedOptions[0] : null)
    : selectedOptions[0];
  const hasSelection = selectedOptions.length > 0;
  const selectedLabel = multiple && selectedOptions.length > 1
    ? selectedOptions.map(option => option.label).join(', ')
    : selected?.label;
  const isNeutralSelected = selectedOptions.length === 1 && selected?.variant === 'neutral';
  const floatingLabel = label || normalizeFloatingLabel(placeholder, 'Выбор');

  const handleOptionChange = (option) => {
    if (!multiple) {
      onChange(option.value);
      setOpen(false);
      return;
    }

    const currentValues = Array.isArray(value) ? value.map(item => `${item}`) : [];
    const optionValue = `${option.value}`;
    let nextValues;
    if (option.exclusive) {
      nextValues = [optionValue];
    } else if (selectedValues.has(optionValue)) {
      nextValues = currentValues.filter(item => item !== optionValue);
    } else {
      nextValues = [
        ...currentValues.filter(item => !options.some(candidate => candidate.exclusive && `${candidate.value}` === item)),
        optionValue
      ];
    }
    if (nextValues.length === 0) {
      const fallbackOption = options.find(candidate => candidate.exclusive);
      nextValues = fallbackOption ? [`${fallbackOption.value}`] : [];
    }
    onChange(nextValues);
  };

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleToggle = () => {
    if (disabled) return;
    if (!open && ref.current) {
      if (preferredDirection === 'up' || preferredDirection === 'down') {
        setDirection(preferredDirection);
        setOpen(v => !v);
        return;
      }
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const headerHeight = menuLabel ? 40 : 0;
      const dropdownHeight = Math.min(headerHeight + options.length * 48, 240);
      setDirection(spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? 'up' : 'down');
    }
    setOpen(v => !v);
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <FloatingField
        label={floatingLabel}
        initialHint={!hasSelection && !!placeholder}
        filled={hasSelection}
        active={open}
        disabled={disabled}
        error={!!error}
      >
        <button
          type="button"
          onClick={handleToggle}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={`w-full px-4 py-3 border rounded-xl flex items-center justify-between focus:outline-none focus:ring-2 transition-colors text-sm ${
          disabled
            ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700 cursor-not-allowed'
            : error
            ? 'bg-white dark:bg-gray-900 border-red-300 focus:ring-red-500 dark:border-red-500 dark:focus:ring-red-500'
            : isNeutralSelected
            ? 'bg-white dark:bg-gray-900 border-gray-300 focus:ring-blue-500 dark:border-gray-700 dark:focus:ring-blue-500'
            : hasSelection
            ? 'bg-white dark:bg-gray-900 border-blue-300 focus:ring-blue-500 dark:border-blue-500 dark:focus:ring-blue-500'
            : 'bg-white dark:bg-gray-900 border-gray-300 focus:ring-blue-500 dark:border-gray-700 dark:focus:ring-blue-500'
        } ${buttonClassName}`}
      >
        <div className="flex min-w-0 items-center">
          {selected?.icon && (
            typeof selected.icon === 'string' ? (
              <span className="mr-2 text-lg">{selected.icon}</span>
            ) : (
              <selected.icon className="mr-2 h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
            )
          )}
          <span className={`floating-field__placeholder truncate ${hasSelection ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'}`}>
            {hasSelection ? selectedLabel : (placeholder || '\u00a0')}
          </span>
        </div>
        <ChevronDown className={`ml-2 h-5 w-5 shrink-0 text-gray-400 dark:text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </FloatingField>

      <AnimatePresence>
        {open && (
        <motion.div
          initial={{ opacity: 0, y: -8, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.95 }}
          transition={{ duration: 0.15 }}
          role="listbox"
          className={`absolute z-20 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl shadow-lg max-h-60 overflow-y-auto ${
            direction === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
          } ${menuClassName}`}
        >
          {menuLabel && (
            <div className={`border-b border-gray-100 bg-gray-100 px-4 py-2 text-xs font-medium text-gray-600 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-300 ${menuHeaderClassName}`}>
              {menuLabel}
            </div>
          )}
          {options.map(opt => (
            <motion.button
              key={opt.value}
              role="option"
              aria-selected={selectedValues.has(`${opt.value}`)}
              type="button"
              onClick={() => handleOptionChange(opt)}
              className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 focus:bg-gray-50 dark:focus:bg-gray-700 focus:outline-none text-sm border-b border-gray-100 dark:border-gray-700 last:border-b-0 flex items-center ${
                selectedValues.has(`${opt.value}`)
                  ? opt.variant === 'neutral'
                    ? 'font-medium text-gray-900 dark:text-gray-100'
                    : 'font-semibold text-blue-600 dark:text-blue-400'
                  : 'text-gray-900 dark:text-gray-100'
              } ${itemClassName}`}
              whileTap={{ scale: 0.98 }}
            >
              {opt.icon && (
                typeof opt.icon === 'string' ? (
                  <span className="mr-2 text-lg">{opt.icon}</span>
                ) : (
                  <opt.icon className="mr-2 h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
                )
              )}
              <span className="min-w-0 truncate">{opt.label}</span>
              {selectedValues.has(`${opt.value}`) && <Check className="w-4 h-4 ml-auto text-green-500" />}
            </motion.button>
          ))}
        </motion.div>
        )}
      </AnimatePresence>
      {error && <p className="text-red-600 dark:text-red-400 text-xs mt-1">{error}</p>}
    </div>
  );
}
